/**
 * THE GUARANTEED SHAPE'S TWO HALVES, and who is offered it.
 *
 * "The full amount to your family, whenever you die" is not one annuity but two products bought
 * together: a lifetime annuity whose own death benefit runs down on the capital access schedule, and
 * a life policy that pays whatever the annuity would not. The pair is priced as one, so the INCOME
 * can be read off the published rate sheet (`annuityRates.ts`); the MEANS TEST cannot, because the
 * two halves are counted differently and only the provider prices how a client's money divides
 * between them. Challenger's own calculator carries that division for every age and sex, and
 * `constants/carePlusSplit.generated.ts` is that table, imported by `scripts/import-careplus-split.py`.
 *
 * THE FAULT THIS CLOSES (27 Sep 2026, owner-caught). The app showed the guaranteed shape as a card,
 * priced its income, and then modelled nothing: the figures beneath the card were for a different
 * shape, and nothing reached the projection. That was worse than not showing it. Before 25 Sep it
 * had modelled "100% back for life" on a plain annuity, which is a product nobody sells; this is
 * the product that actually does it, modelled from the provider's own numbers.
 */

import { CAREPLUS_SPLIT } from './constants/carePlusSplit.generated.ts';

export interface CarePlusSplit {
    /** Share of the total invested that buys the life policy, 0 to 1. */
    insurance: number;
    /** Share of the total invested that buys the lifetime annuity, 0 to 1. */
    annuity: number;
    /** True when the age sits outside the table and the nearest edge was used. */
    clamped: boolean;
}

type SplitRow = { readonly insurance: number; readonly annuity: number };

/**
 * How a client's money divides at this age. The table runs 64 to 100 by single year; an age between
 * two rows is interpolated, and an age outside the table takes the nearest edge and says so. It is
 * clamped rather than extrapolated because the shares are bounded by construction — they sum to one
 * — and carrying a slope past 100 would soon put the annuity share below zero.
 */
export function carePlusSplitFor(age: number, sex: 'male' | 'female' | null | undefined): CarePlusSplit {
    /*
     * AN UNKNOWN SEX READS AS MALE HERE — the OPPOSITE of the rate table's default, and I first wrote
     * it the wrong way round (27 Sep 2026, caught the same day while explaining the figure to the
     * owner). The principle is the same everywhere: an unrecorded sex must never flatter the
     * strategy. For the RATE that means female, because a woman's payment is lower and so the income
     * benefit is understated. For the SPLIT it means male, because a man's insurance share is the
     * HIGHER one at every age, so more of the money is counted at cost, assessed assets are higher,
     * and the pension benefit is understated. Reading an unknown client as female here did the
     * reverse: it counted less, lifted the pension, and overstated what the guarantee buys.
     */
    const table = (sex === 'female' ? CAREPLUS_SPLIT.female : CAREPLUS_SPLIT.male) as unknown as Record<string, SplitRow>;
    const ages = Object.keys(table).map(Number).sort((a, b) => a - b);
    // Every age below is read from the table itself (or clamped into its range), so a miss is a bug;
    // the checked lookups say so rather than letting an undefined become NaN in the means test.
    const first = ages[0];
    const last = ages[ages.length - 1];
    if (first === undefined || last === undefined) throw new Error('CarePlus split table is empty');

    const at = (a: number): SplitRow => {
        const row = table[String(a)];
        if (!row) throw new Error(`CarePlus split table has no row for age ${a}`);
        return row;
    };
    if (!Number.isFinite(age)) return { ...at(last), clamped: true };
    if (age <= first) return { ...at(first), clamped: age < first };
    if (age >= last) return { ...at(last), clamped: age > last };

    const lo = Math.floor(age);
    const hi = Math.ceil(age);
    if (lo === hi) return { ...at(lo), clamped: false };
    const t = age - lo;
    const a = at(lo);
    const b = at(hi);
    const insurance = a.insurance + (b.insurance - a.insurance) * t;
    return { insurance, annuity: 1 - insurance, clamped: false };
}

/** When the split table was struck, for the screens and the pack. */
export function carePlusSplitSheet(): { source: string; validTo: string | null; importedAt: string } {
    return { source: CAREPLUS_SPLIT.source, validTo: CAREPLUS_SPLIT.validTo, importedAt: CAREPLUS_SPLIT.importedAt };
}

/**
 * Whether the guaranteed shape is offered to this client at all.
 *
 * IT IS A RULE ABOUT THE CLIENT, NOT ABOUT THE SCREEN. The product is sold only to people receiving,
 * or about to receive, government-subsidised aged care. That is not the same as "the aged care
 * module": Support at Home IS government-subsidised aged care and qualifies on the same footing as a
 * residential place, and a village or downsizing client with a care need recorded qualifies too. So
 * the test reads the fact-find rather than the module the client happens to be in. It lives here,
 * in shared code, because the suggestion engines run on the server and need the same answer the
 * screens give.
 */
export function guaranteedShapeAvailable(factFind: any, owner: 'client' | 'partner' | 'joint' = 'client'): boolean {
    const person = (who: 'client' | 'partner'): boolean => {
        const acat = factFind?.careNeeds?.[who === 'client' ? 'clientAcat' : 'partnerAcat'];
        const residency = factFind?.[who]?.currentResidency;
        const inResidentialCare = residency === 'aged_care' || residency === 'residential_care';
        const onSupportAtHome = residency === 'home_care' || residency === 'support_at_home';
        // An approval, or an application in train, counts: the shape is bought at the point of entry.
        const approved = acat?.status === 'yes' || acat?.status === 'applied' || acat?.status === 'assessed';
        return inResidentialCare || onSupportAtHome || approved;
    };
    if (owner === 'joint') return person('client') || person('partner');
    return person(owner);
}
